import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Box } from "@mui/material";
import { Stack } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import Button, { Color } from "../../../../../components/Form/Button";
import Input from "../../../../../components/Form/Input";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { IS_TERMINATED } from "../../../../../constants";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { Error } from "../../../../../models/error";
import { validateForm } from "../../actions";
import CountdownTimer from "./CountdownTimer";
import useStyles, {
  DangerContainer,
  ErrorContent,
  ErrorIcon,
  ErrorIconContainer,
  ErrorText,
  ErrorTextContainer,
  ForgotLink,
  HiddenText,
  TimerContainer,
} from "./styles";

const DANGER_ICON = require("../../../../../assets/icons/danger.svg");

interface SigninFormProps {
  errorReturned?: Error;
  isLoading?: boolean;
  handleSubmit?: (event: React.FormEvent) => void;
}

export interface SigninFormHandler {
  email: string;
  password: string;
}

const SigninForm: ForwardRefRenderFunction<SigninFormHandler, SigninFormProps> = (props, ref) => {
  const { errorReturned, isLoading, handleSubmit } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [isLoginEnabled, setIsLoginEnabled] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [timer, setStartTimer] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const ATTEMPTS_LIMIT = 6;

  const [isTerminated, setIsTerminated] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  useImperativeHandle(ref, () => ({
    email,
    password,
  }));

  useEffect(() => {
    const isTerminatedFlag = window.localStorage.getItem(IS_TERMINATED);

    if (isTerminatedFlag === "true") {
      setIsTerminated(true);
    } else {
      setIsTerminated(false);
    }
    localStorage.removeItem(IS_TERMINATED);
  }, []);

  const hasErrorReturned = () => !!errorReturned;

  const getErrorReturned = () =>
    errorReturned?.message?.length ? errorReturned.message : translate("oopsIncorrectCredentials");

  const hasErrorForAccount = () => {
    if (errorReturned && errorReturned.field === "user") return true;
  };
  const getErrorReturnedFailedLoginAttempts = () => {
    if (errorReturned) return errorReturned.failedLoginAttempts;
  };

  const getTheLockoutDurationValue = () => {
    if (errorReturned) return errorReturned.lockoutTime;
  };

  const onIntervalComplete = (val: boolean) => {
    setIsLoginEnabled(val);
    if (!val) {
      setStartTimer(false);
      window.location.replace("/");
    }
  };

  const handleSubmitForm = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (validateForm(translate, { email, password }, setErrors)) {
      if (handleSubmit) handleSubmit(event);
    }
  };

  const getButtonDisabled = () => {
    return isLoginEnabled;
  };

  const reachedTheLimitOfAttempts = useMemo(
    () => Number(errorReturned?.failedLoginAttempts ?? 0) >= ATTEMPTS_LIMIT,
    [errorReturned]
  );

  useEffect(() => {
    if (isSubmitted) validateForm(translate, { email, password }, setErrors);
  }, [isSubmitted, email, password]);

  useEffect(() => {
    setStartTimer(true);
  }, [getTheLockoutDurationValue(), getErrorReturnedFailedLoginAttempts()]);

  useEffect(() => {
    if (
      window.location.href.includes("/signin?continue") &&
      window.location.href.includes("accept-invite")
    ) {
      NotificationToast.info(translate("youMustBeLoggedInToSeeTheInvitation"), { duration: 8000 });
    }
  }, []);

  const lockoutLabel = (seconds: number = 0) => {
    if (seconds < 60) return `${seconds} ${translate("seconds")}`;
    if (seconds > 60) return `${seconds / 60} ${translate("minutes")}`;

    return `1 ${translate("minute")}`;
  };

  const genericErrorMessage = errorReturned?.message;
  const shouldShowGenericError = !!genericErrorMessage && !hasErrorForAccount() && !isTerminated;

  return (
    <Box className={classes.boxContainer}>
      <Stack
        className={classes.mainGrid}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={3}
      >
        {!(hasErrorForAccount() && timer) && !isTerminated && (
          <h2 className={classes.title}>{translate("welcomeBack")}</h2>
        )}
        {hasErrorForAccount() && timer && (
          <DangerContainer>
            <ErrorContent>
              <ErrorIconContainer>
                <ErrorIcon src={DANGER_ICON} alt="Error icon" />
              </ErrorIconContainer>
              <ErrorTextContainer>
                <ErrorText variant="body2">
                  <Trans
                    i18nKey="couldNotFoundWithUsernamePassword"
                    values={{
                      numberOfAttempts: getErrorReturnedFailedLoginAttempts(),
                      lockoutDuration: lockoutLabel(getTheLockoutDurationValue()),
                    }}
                    components={{
                      Link: <ForgotLink to="/forgot" />,
                      HiddenText: <HiddenText hide={!reachedTheLimitOfAttempts} />,
                      strong: <strong />,
                    }}
                  />
                </ErrorText>
              </ErrorTextContainer>
              {timer && (
                <TimerContainer>
                  <CountdownTimer
                    initialTime={getTheLockoutDurationValue() ?? 0}
                    onIntervalComplete={onIntervalComplete}
                    lockoutDuration={getTheLockoutDurationValue() ?? 0}
                  />
                </TimerContainer>
              )}
            </ErrorContent>
          </DangerContainer>
        )}

        {isTerminated && (
          <DangerContainer>
            <ErrorContent>
              <ErrorIconContainer>
                <ErrorIcon src={DANGER_ICON} alt="Error icon" />
              </ErrorIconContainer>
              <ErrorTextContainer>
                <ErrorText variant="body2">{translate("terminatedSession")}</ErrorText>
                <ErrorText variant="body2">
                  <Trans i18nKey="reEnterCreds" components={{ strong: <strong /> }} />
                </ErrorText>
              </ErrorTextContainer>
            </ErrorContent>
          </DangerContainer>
        )}

        {shouldShowGenericError && (
          <DangerContainer data-cy="signinGenericError">
            <ErrorContent>
              <ErrorIconContainer>
                <ErrorIcon src={DANGER_ICON} alt="Error icon" />
              </ErrorIconContainer>
              <ErrorTextContainer>
                <ErrorText variant="body2">{genericErrorMessage}</ErrorText>
              </ErrorTextContainer>
            </ErrorContent>
          </DangerContainer>
        )}

        <form className="w-100" onSubmit={handleSubmitForm}>
          <Stack width="100%" justifyContent="center" alignItems="flex-start" spacing={3}>
            <Input
              fullWidth
              required
              inputProps={{
                "aria-label": "user-mail",
                autocomplete: "username",
              }}
              label={translate("emailOrUsername")}
              value={email}
              name="user-mail"
              id="user-mail"
              data-cy="signInEmail"
              placeholder={translate("sampleEmailCom")}
              onChange={(event) => setEmail(event.target.value)}
              error={fieldHasError("email") || hasErrorReturned()}
              errorMessage={getErrorByField("email") || getErrorReturned()}
            />
            <Input
              fullWidth
              required
              inputProps={{
                "aria-label": "user-pwd",
                autoComplete: "off",
              }}
              type="password"
              label={translate("password")}
              value={password}
              name="user-pwd"
              id="pwd"
              data-cy="signInPassword"
              placeholder="*******"
              onChange={(event) => setPassword(event.target.value)}
              error={fieldHasError("password") || hasErrorReturned()}
              errorMessage={getErrorByField("password") || getErrorReturned()}
            />
            <Stack width="100%" direction={{ xs: "column", md: "row" }} justifyContent="flex-end">
              <span
                className={classes.clickableText}
                data-cy="forgotPasswordLink"
                aria-label="forgotPasswordLink"
                onClick={() => history.push("/forgot")}
              >{`${translate("forgotPassword")}?`}</span>
            </Stack>
            <Button
              type="submit"
              fullWidth
              customColor={Color.BRAND}
              disabled={getButtonDisabled()}
              loading={isLoading}
              data-cy="signInButton"
              aria-label="signInButton"
            >
              {translate("login")}
            </Button>
          </Stack>
        </form>
        <div className={classes.createAccount}>
          <span className={classes.text}>{`${translate("notRegisteredYet")}`}&nbsp;</span>
          <span
            className={classes.clickableText}
            data-cy="forgotSignupLink"
            aria-label="forgotSignupLink"
            onClick={() => history.push("/signup")}
          >
            {translate("createAnAccount")}
          </span>
        </div>
      </Stack>
    </Box>
  );
};

export default forwardRef(SigninForm);
